import { useState } from "react";
import { Check, KeyRound, Unlink } from "lucide-react";
import { Alert } from "@/components/common/Alert/Alert";
import { Button } from "@/components/shadcn/button";
import { Input } from "@/components/shadcn/input";
import { Label } from "@/components/shadcn/label";
import { ConfirmDialog } from "@/components/common/Dialog";
import { MicrosoftIcon } from "@/components/logos/MicrosoftIcon";
import { PasswordVisibilityToggle } from "@/components/common/PasswordVisibilityToggle";
import { showErrorToast, showSuccessToast } from "@/hooks/use-toast";
import { PASSWORD_MAX_INPUT_LENGTH, PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, validateNewPassword } from "@/pages/login/utils/validators";
import { changePassword, disconnectSso, startSsoConnect } from "../api";
import type { AccountSettings } from "../types";
import { AccountSettingsSectionHeading } from "./AccountSettingsSectionHeading";
import { SignInMethodPanel } from "./SignInMethodPanel";

type SignInMethodsCardProps = {
  settings: AccountSettings;
  onChanged: () => Promise<void>;
};

export const SignInMethodsCard = ({ settings, onChanged }: SignInMethodsCardProps) => {
  const { sso, canChangePassword } = settings;

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswords, setShowPasswords] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const mismatch = newPassword !== "" && confirmPassword !== "" && newPassword !== confirmPassword;
  const invalidLength = newPassword !== "" && validateNewPassword(newPassword) !== true;
  const canSubmit = !isSaving && currentPassword !== "" && newPassword !== ""
    && !invalidLength && !mismatch && newPassword === confirmPassword;

  const savePassword = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;

    setIsSaving(true);
    try {
      await changePassword({ currentPassword, newPassword });
      showSuccessToast("Password changed.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : "Failed to change password.");
    } finally {
      setIsSaving(false);
    }
  };

  const link = async () => {
    setIsRedirecting(true);
    try {
      window.location.assign(await startSsoConnect());
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : "Failed to start Microsoft sign-in linking.");
      setIsRedirecting(false);
    }
  };

  const unlink = async () => {
    setIsUnlinking(true);
    try {
      await disconnectSso();
      showSuccessToast("Microsoft sign-in unlinked.");
      setConfirmOpen(false);
      await onChanged();
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : "Failed to unlink Microsoft sign-in.");
    } finally {
      setIsUnlinking(false);
    }
  };

  const ssoBusy = isRedirecting || isUnlinking;

  return (
    <div className="min-w-0">
      <AccountSettingsSectionHeading
        title="How you sign in"
        subtitle="Only one sign-in method is active at a time."
      />

      <div className="grid gap-3">
        {/* Only offered when the account has an SSO provider configured. */}
        {sso.isAvailable && (
          <SignInMethodPanel
            icon={<MicrosoftIcon size={24} />}
            title="Sign in with Microsoft"
            description={sso.error
              ? "We can't show the current state of this connection."
              : sso.isLinked
                ? `Signing in with ${sso.email ?? "your Microsoft account"}.`
                : "Use your Microsoft account instead of a Klipboard CRM password."}
            isActive={sso.isLinked}
            isStateUnknown={!!sso.error}
            actions={sso.isLinked ? (
              <span title={sso.canUnlinkReason ?? undefined}>
                <Button variant="outline" disabled={ssoBusy || !sso.canUnlink}
                  onClick={() => setConfirmOpen(true)}>
                  <Unlink className="size-4" aria-hidden />
                  Unlink
                </Button>
              </span>
            ) : (
              <span title={sso.canLinkReason ?? undefined}>
                <Button onClick={link} disabled={ssoBusy || !sso.canLink}>
                  <MicrosoftIcon size={18} />
                  {isRedirecting ? "Redirecting..." : "Link Microsoft account"}
                </Button>
              </span>
            )}
          >
            {sso.error && <Alert variant="error" persistent>{sso.error}</Alert>}
          </SignInMethodPanel>
        )}

        <SignInMethodPanel
          icon={<KeyRound className="size-5 text-muted-foreground" aria-hidden />}
          title="Klipboard CRM password"
          description={canChangePassword
            ? "Sign in with your Klipboard CRM username and password."
            : "Managed by your Microsoft account. Unlink it to sign in with a password again."}
          isActive={canChangePassword}
        >
          {canChangePassword && (
            <form onSubmit={savePassword}>
              <div className="flex max-w-sm flex-col gap-4">
                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="currentPassword">Current password</Label>
                    <PasswordVisibilityToggle showPass={showPasswords}
                      onToggle={() => setShowPasswords((visible) => !visible)} />
                  </div>
                  <Input id="currentPassword" className="mt-1 bg-background"
                    type={showPasswords ? "text" : "password"}
                    autoComplete="current-password" value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)} />
                </div>

                <div>
                  <Label htmlFor="newPassword">New password</Label>
                  <Input id="newPassword" className="mt-1 bg-background"
                    type={showPasswords ? "text" : "password"}
                    autoComplete="new-password" value={newPassword} aria-invalid={invalidLength}
                    maxLength={PASSWORD_MAX_INPUT_LENGTH} aria-describedby="newPasswordHint"
                    onChange={(e) => setNewPassword(e.target.value)} />
                  <p id="newPasswordHint" className={`mt-1 text-sm ${invalidLength ? "text-destructive" : "text-muted-foreground"}`}>
                    {`Use ${PASSWORD_MIN_LENGTH} to ${PASSWORD_MAX_LENGTH} characters. A passphrase of several words works well.`}
                  </p>
                </div>

                <div>
                  <Label htmlFor="confirmPassword">Confirm new password</Label>
                  <Input id="confirmPassword" className="mt-1 bg-background"
                    type={showPasswords ? "text" : "password"}
                    autoComplete="new-password" value={confirmPassword} aria-invalid={mismatch}
                    maxLength={PASSWORD_MAX_INPUT_LENGTH}
                    onChange={(e) => setConfirmPassword(e.target.value)} />
                  {mismatch && <p className="mt-1 text-sm text-destructive">Passwords must match.</p>}
                </div>

                <div>
                  <Button type="submit" disabled={!canSubmit}>
                    <Check className="size-4" aria-hidden />
                    {isSaving ? "Saving..." : "Change your password"}
                  </Button>
                </div>
              </div>
            </form>
          )}
        </SignInMethodPanel>
      </div>

      <hr className="pb-4 mt-4" />

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        hideTrigger
        title="Unlink Microsoft sign-in?"
        description="You'll sign in with your Klipboard CRM username and password again. Make sure you know your password before continuing."
        confirmText="Unlink"
        loading={isUnlinking}
        onConfirm={unlink}
      />
    </div>
  );
};
